import { useState, memo, useRef, useEffect } from 'react';
import { Input } from '@/sub-luca-plugin/compatible-components';
import ScrollView from '@/sub-luca-plugin/compatible-components/scroll-view';
import { SubjectSearchItemIconSvg, SearchOutlined } from '@/sub-luca-plugin/compatible-components/svg-icons';
import NavigationHeaderWapper from '@/sub-luca-plugin/components/navigation-header-wapper';
import { luca_ask_similar_problems_search } from '@/sub-luca-plugin/apis/qa';
import { ISimilarQuestionBySearchItem } from '@/sub-luca-plugin/types';
import { navigateExample } from '@/sub-luca-plugin/utils/class-example';
import { tCommon } from '@/sub-luca-plugin/i18n';
import './index.scss';

interface IProps {
    projectId: number;
    subjectId: number;
    onClose?: () => void;
}

export const SimilarSearchRender = ({ projectId, subjectId, onClose }: IProps) => {
    const scrollContainer = useRef<HTMLDivElement>(null);
    const pagination = useRef<{ pageNum: number; pageSize: number }>({ pageNum: 1, pageSize: 20 });
    const loading = useRef(false);
    const [list, setList] = useState<ISimilarQuestionBySearchItem[]>();
    const [searchValue, setSearchValue] = useState('');
    const [noNextPage, setNoNextPage] = useState(false);

    const getSearchContent = async (question: string) => {
        if (loading.current) return;
        loading.current = true;
        const { result } = await luca_ask_similar_problems_search({
            projectId,
            subjectId,
            question,
            ...pagination.current,
        });
        const { items = [] } = result || {};
        const newItems = items.map(item => ({
            ...item,
            ask: item.ask.replace(/<em>([^<em>|<\/em>]*)<\/em>/g, '<span class="similar-search__question-describe--mark">$1</span>'),
        }));
        const isFirstPage = pagination.current.pageNum === 1;
        setList(oldList => {
            const newList = isFirstPage ? newItems : (oldList || []).concat(newItems);
            return newList;
        });
        setNoNextPage(items.length === 0);
        isFirstPage && scrollContainer.current?.scrollTo?.({ top: 0 });
        pagination.current.pageNum++;
        loading.current = false;
    };

    const handleSearchValue = () => {
        if (!searchValue) return;
        pagination.current.pageNum = 1;
        getSearchContent(searchValue);
    };

    const handleClose = () => {
        pagination.current.pageNum = 1;
        loading.current = false;
        setSearchValue('');
        setList(undefined);
        setNoNextPage(false);
        onClose?.();
    };

    const handleViewDetail = (askId: number) => {
        navigateExample.linkTo('gbot_detail', { id: askId });
    };

    const handleInputChange = (ev: any) => {
        const value = ev.target.value;
        if (value === '') {
            setList(undefined);
        }
        setSearchValue(ev.target.value);
    };

    const onScrollToLower = () => {
        if (noNextPage) return;
        getSearchContent(searchValue);
    };

    return (
        <div className="similar-search">
            <NavigationHeaderWapper onClose={handleClose}>
                <div className="similar-search__title">{tCommon.t('searchBySubject', '学科搜索')}</div>
            </NavigationHeaderWapper>
            <div className="similar-search__input-box">
                <Input
                    value={searchValue}
                    placeholder={tCommon.t('inputKeywords', '输入关键词')}
                    allowClear
                    autoFoucs
                    maxLength={2000}
                    prefix={<SearchOutlined style={{ color: '#CCCCCC' }} />}
                    onChange={handleInputChange}
                    onPressEnter={handleSearchValue}
                />
                <div onClick={handleSearchValue}>{tCommon.t('searchAction', '搜索')}</div>
            </div>
            <div className="similar-search__content">
                {list?.length ? (
                    <ScrollView className="similar-search__scroll-container" scrollY lowerThreshold={100} onScrollToLower={onScrollToLower}>
                        <div className="similar-search__search-content">
                            {list.map((item, index) => {
                                return (
                                    <div
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={`${item.askId}-${index}`}
                                        className="similar-search__search-item"
                                        onClick={() => handleViewDetail(item.askId)}
                                    >
                                        <SubjectSearchItemIconSvg className="similar-search__answer-icon" />
                                        <div className="similar-search__answer-info">
                                            <div
                                                className="similar-search__question-describe"
                                                dangerouslySetInnerHTML={{ __html: item.ask }}
                                            ></div>
                                            <div className="similar-search__other-info">
                                                {item.transferLaborState === 1 && (
                                                    <div className="similar-search__qa-for-teacher-icon">
                                                        {tCommon.t('expertTeacher', '名师')}
                                                    </div>
                                                )}
                                                <div className="similar-search__create-time">{item.createTime}</div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                            {noNextPage && <div className="similar-search__no-more-data">{tCommon.t('noMoreContent', '没有更多了～')}</div>}
                        </div>
                    </ScrollView>
                ) : (
                    <div className="similar-search__no-content">
                        {list?.length === 0 ? (
                            <div className="similar-search__no-data-tips" />
                        ) : (
                            <div className="similar-search__search-tips" />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default memo(SimilarSearchRender);
